import { z } from 'zod';
/**
 * Emptying and refilling an organization.
 *
 * Two scopes, because they answer two different questions. **Activity** is everything people
 * produced: time entries, running timers, the personal activities they created for themselves,
 * the reports generated from all of it and the notifications about those. **Accounts** is the
 * people themselves — their logins, sessions and group memberships.
 *
 * The configuration an organization is set up with — its groups, projects and shared activities —
 * survives both, so an emptied organization is still ready to use.
 */
export const RESET_SCOPES = ['ACTIVITY', 'ACCOUNTS'];
export const resetRequestSchema = z.object({
    scopes: z.array(z.enum(RESET_SCOPES)).min(1).max(RESET_SCOPES.length),
    /**
     * The organization's name, typed by the administrator. Not a password — a speed bump, so that
     * "clear the test data" cannot happen on the wrong tab.
     */
    confirm: z.string().min(1).max(200),
});
/** What a reset removed, or (in the counts endpoint) what one would remove today. */
export const resetCountsSchema = z.object({
    timeEntries: z.number().int().nonnegative(),
    runningTimers: z.number().int().nonnegative(),
    personalActivities: z.number().int().nonnegative(),
    reports: z.number().int().nonnegative(),
    notifications: z.number().int().nonnegative(),
    users: z.number().int().nonnegative(),
    groupMemberships: z.number().int().nonnegative(),
    sessions: z.number().int().nonnegative(),
});
export const resetResultSchema = z.object({
    scopes: z.array(z.enum(RESET_SCOPES)),
    removed: resetCountsSchema,
});
/** What the organization holds right now, so a confirmation can say what is about to go. */
export const maintenanceStatsSchema = z.object({
    organization: z.object({ id: z.string(), name: z.string() }),
    activity: resetCountsSchema.pick({
        timeEntries: true,
        runningTimers: true,
        personalActivities: true,
        reports: true,
        notifications: true,
    }),
    accounts: z.object({
        users: z.number().int().nonnegative(),
        /** Accounts a reset would leave behind: yours. */
        retained: z.number().int().nonnegative(),
        groupMemberships: z.number().int().nonnegative(),
    }),
    /** Kept by every reset: an emptied organization is still configured. */
    configuration: z.object({
        groups: z.number().int().nonnegative(),
        projects: z.number().int().nonnegative(),
        sharedActivities: z.number().int().nonnegative(),
    }),
});
// ---------------------------------------------------------------- workbook import
export const WORKBOOK_SHEETS = [
    'Users',
    'Groups',
    'Memberships',
    'Projects',
    'Activities',
    'Assignments',
    'Time entries',
];
export const importIssueSchema = z.object({
    /** Row number as the spreadsheet shows it, so "line 42" means line 42. */
    row: z.number().int(),
    message: z.string(),
});
export const importSheetResultSchema = z.object({
    sheet: z.string(),
    /** Absent from the file entirely — not an error: a partial workbook is a valid thing to import. */
    missing: z.boolean(),
    created: z.number().int().nonnegative(),
    updated: z.number().int().nonnegative(),
    /** Already present and identical; re-importing the same file is a no-op. */
    skipped: z.number().int().nonnegative(),
    errors: z.array(importIssueSchema),
});
export const importWorkbookResultSchema = z.object({
    dryRun: z.boolean(),
    sheets: z.array(importSheetResultSchema),
    totals: z.object({
        created: z.number().int().nonnegative(),
        updated: z.number().int().nonnegative(),
        skipped: z.number().int().nonnegative(),
        errors: z.number().int().nonnegative(),
    }),
});
//# sourceMappingURL=maintenance.js.map